import React, { useState } from "react";
import Layout from "../components/Layout";
import { Phone, Mail, MapPin, Clock, Send, Shield } from "lucide-react";

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    humanAnswer: "",
  });

  const [files, setFiles] = useState<File[]>([]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Creează FormData pentru fișiere
      const submitData = new FormData();

      // Adaugă datele din formular
      Object.entries(formData).forEach(([key, value]) => {
        submitData.append(key, value);
      });

      // Adaugă fișierele
      files.forEach((file) => {
        submitData.append("files", file);
      });

      const response = await fetch(
        "https://felixdent-backend.onrender.com/api/contact",
        {
          method: "POST",
          body: submitData, // NU mai pune Content-Type, FormData se ocupă
        },
      );

      const result = await response.json();

      if (result.success) {
        setShowSuccess(true);
        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
          humanAnswer: "",
        });
        setFiles([]); // Resetează și fișierele
        setTimeout(() => {
          setShowSuccess(false);
        }, 3000);
      } else {
        alert("Eroare: " + result.error);
      }
    } catch (error) {
      alert("Eroare de server. Încearcă din nou.");
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="min-h-screen text-gray-900 dark:text-white transition-all duration-300">
        <main className="flex-grow px-4 py-16">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-[#025951] dark:text-emerald-400 uppercase tracking-wide">
              Contact Us
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Get in touch with our dental technology experts. We're here to
              help with your next project.
            </p>
            <div className="w-24 h-1 bg-green dark:bg-gradient-to-r from-[#025951] to-emerald-400 mx-auto rounded-full mt-6"></div>
          </div>

          <div className="max-w-7xl mx-auto grid lg:grid-cols-3 gap-8">
            {/* Contact Information */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-white dark:bg-neutral-600 p-6 rounded-2xl shadow-lg border border-gray-200 dark:border-neutral-500">
                <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
                  Get in Touch
                </h2>

                <div className="space-y-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-green dark:bg-gradient-to-br from-[#025951] to-emerald-400 rounded-lg flex items-center justify-center">
                      <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">
                        Phone
                      </p>
                      <a
                        href="tel:+447305959777"
                        className="text-[#025951] dark:text-emerald-400 hover:underline"
                      >
                        +44 7305 959777
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-green dark:bg-gradient-to-br from-[#025951] to-emerald-400 rounded-lg flex items-center justify-center">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">
                        Email
                      </p>
                      <a
                        href="mailto:info@felixdent.com"
                        className="text-[#025951] dark:text-emerald-400 hover:underline"
                      >
                        info@felixdent.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-green dark:bg-gradient-to-br from-[#025951] to-emerald-400 rounded-lg flex items-center justify-center">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">
                        Business Hours
                      </p>
                      <p className="text-gray-600 dark:text-gray-300">
                        Monday - Friday
                        <br />
                        9:00 AM - 5:00 PM
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-green dark:bg-gradient-to-br from-[#025951] to-emerald-400 rounded-lg flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">
                        Location
                      </p>
                      <a
                        href="https://www.google.com/maps/place/FELIXDENT/@51.6127008,0.0877,17z/data=!3m1!4b1!4m6!3m5!1s0x487613def2efe5a3:0x1dd7187df26d93f3!8m2!3d51.6127008!4d0.0902749!16s%2Fg%2F11kqs2tjw6?entry=ttu&g_ep=EgoyMDI1MDkwMy4wIKXMDSoASAFQAw%3D%3D"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#025951] dark:text-emerald-400 hover:underline hover:scale-105 transition-all duration-300 cursor-pointer"
                      >
                        159b Manor Rd, London IG7 5QA
                        <br />
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Click to view on Google Maps
                        </span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Contact */}
              <div
                className="bg-green-500 
                dark:bg-gradient-to-br 
                dark:from-emerald-500 
                dark:to-emerald-400 
                text-white p-6 rounded-2xl shadow-lg"
              >
                <h3 className="text-xl font-bold mb-4">Need Immediate Help?</h3>
                <p className="mb-4 opacity-90">
                  Contact us directly via WhatsApp for quick responses.
                </p>
                <a
                  href="https://wa.me/447305959777?text=Hello%2C%20I%20would%20like%20to%20inquire%20about%20your%20dental%20services."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white text-[#025951] dark:bg-neutral-800 dark:text-emerald-400 px-6 py-3 rounded-full font-semibold hover:scale-105 transition-all duration-300 shadow-lg inline-block"
                >
                  WhatsApp Us
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white dark:bg-neutral-600 p-8 rounded-2xl shadow-lg border border-gray-200 dark:border-neutral-500">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-green dark:bg-gradient-to-br from-[#025951] to-emerald-400 rounded-lg flex items-center justify-center mr-4">
                    <Send className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Send us a Message
                  </h2>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Your Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg bg-neutral-50 dark:bg-neutral-600 border border-gray-200 dark:border-neutral-500 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#025951] dark:focus:ring-emerald-400 focus:border-transparent transition-all duration-200"
                        placeholder="Enter your full name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Your Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg bg-neutral-50 dark:bg-neutral-600 border border-gray-200 dark:border-neutral-500 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#025951] dark:focus:ring-emerald-400 focus:border-transparent transition-all duration-200"
                        placeholder="your.email@example.com"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg bg-neutral-50 dark:bg-neutral-600 border border-gray-200 dark:border-neutral-500 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#025951] dark:focus:ring-emerald-400 focus:border-transparent transition-all duration-200"
                        placeholder="+44 7XXX XXXXXX"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Subject *
                      </label>
                      <input
                        type="text"
                        name="subject"
                        required
                        value={formData.subject}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg bg-neutral-50 dark:bg-neutral-600 border border-gray-200 dark:border-neutral-500 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#025951] dark:focus:ring-emerald-400 focus:border-transparent transition-all duration-200"
                        placeholder="What can we help you with?"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      Your Message
                    </label>
                    <textarea
                      name="message"
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg bg-neutral-50 dark:bg-neutral-600 border border-gray-200 dark:border-neutral-500 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#025951] dark:focus:ring-emerald-400 focus:border-transparent transition-all duration-200 resize-none"
                      placeholder="Tell us more about your project or requirements..."
                    />
                  </div>

                  {/* ADAUGĂ DUPĂ textarea message și ÎNAINTE de Security Question */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      Attach Files & Images
                    </label>
                    <div className="border-2 border-dashed border-gray-300 dark:border-neutral-500 rounded-lg p-6 text-center hover:border-[#025951] dark:hover:border-emerald-400 transition-colors">
                      <input
                        type="file"
                        multiple
                        accept="*/*"
                        onChange={(e) => {
                          if (e.target.files) {
                            setFiles(Array.from(e.target.files));
                          }
                        }}
                        className="hidden"
                        id="file-upload"
                      />
                      <label
                        htmlFor="file-upload"
                        className="cursor-pointer flex flex-col items-center"
                      >
                        <div className="w-12 h-12 bg-green dark:bg-gradient-to-br from-[#025951] to-emerald-400 rounded-lg flex items-center justify-center mb-3">
                          <svg
                            className="w-6 h-6 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                            />
                          </svg>
                        </div>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Click to upload files
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          PNG, JPG, PDF, DOC, etc. Max 5MB each
                        </p>
                      </label>
                    </div>

                    {/* Afișează fișierele selectate */}
                    {files.length > 0 && (
                      <div className="mt-3">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Selected files:
                        </p>
                        {files.map((file, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between bg-gray-50 dark:bg-neutral-600 p-2 rounded mb-1"
                          >
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {file.name}
                            </span>
                            <button
                              type="button"
                              onClick={() =>
                                setFiles(files.filter((_, i) => i !== index))
                              }
                              className="text-red-500 hover:text-red-700 text-sm"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Human Verification */}
                  <div className="bg-neutral-50 dark:bg-neutral-600 p-4 rounded-lg border border-gray-200 dark:border-neutral-500">
                    <div className="flex items-center mb-2">
                      <Shield className="w-5 h-5 text-[#025951] dark:text-emerald-400 mr-2" />
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                        Security Question *
                      </label>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      What is the capital of United Kingdom?
                    </p>
                    <input
                      type="text"
                      name="humanAnswer"
                      required
                      value={formData.humanAnswer}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg bg-white dark:bg-neutral-700 border border-gray-200 dark:border-neutral-500 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#025951] dark:focus:ring-emerald-400 focus:border-transparent transition-all duration-200"
                      placeholder="Your answer..."
                    />
                  </div>

                  <div className="pt-4">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full 
           bg-green
           dark:bg-gradient-to-r 
           dark:from-emerald-500 
           dark:to-emerald-400 
           text-white font-semibold py-4 px-8 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                          Sending Message...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center">
                          <Send className="w-5 h-5 mr-2" />
                          Send Message
                        </span>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="max-w-4xl mx-auto mt-16">
            <h2 className="text-3xl font-bold text-center mb-8 text-gray-900 dark:text-white">
              Frequently Asked{" "}
              <span className="text-[#025951] dark:text-emerald-400">
                Questions
              </span>
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white dark:bg-neutral-600 p-6 rounded-xl border border-gray-200 dark:border-neutral-500">
                <h3 className="font-semibold text-lg mb-2 text-gray-900 dark:text-white">
                  What services do you offer?
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  We provide 3D printing, milling services, dental models, and
                  surgical guides for dental professionals.
                </p>
              </div>
              <div className="bg-green-200 dark:bg-neutral-600 p-6 rounded-xl border border-gray-300 dark:border-neutral-500">
                <h3 className="font-semibold text-lg mb-2 text-gray-900 dark:text-white">
                  How long does delivery take?
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  We offer next-day shipping with Royal Mail or DHL for most
                  orders within the UK.
                </p>
              </div>
              <div className="bg-white dark:bg-neutral-600 p-6 rounded-xl border border-gray-200 dark:border-neutral-500">
                <h3 className="font-semibold text-lg mb-2 text-gray-900 dark:text-white">
                  Do you work with international clients?
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Yes, we serve clients worldwide with secure shipping and
                  digital file handling.
                </p>
              </div>
              <div className="bg-green-200 dark:bg-neutral-600 p-6 rounded-xl border border-gray-300 dark:border-neutral-500">
                <h3 className="font-semibold text-lg mb-2 text-gray-900 dark:text-white">
                  How do I get a quote?
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Contact us via phone, WhatsApp, or this form for a free quote
                  on your project.
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>

      {showSuccess && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-neutral-800 p-8 rounded-2xl shadow-2xl text-center max-w-md mx-4">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Thank You!
            </h3>
            <p className="text-gray-600 dark:text-gray-300">
              Message sent successfully!
              <br />
              We will respond to you soon.
            </p>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ContactUs;
